/**
 * The CTA composite field.
 *
 * `analyticsId` is the field that matters. On the live site the button's DOM id and the
 * `pos` argument of `handleConversionClick(loc, pos, utmContent)` are the same string, so
 * this produces one value for both. Two CTAs sharing an id is both invalid HTML and a
 * corrupted funnel, which is why the datalist offers the positions already in use rather
 * than leaving people to invent synonyms.
 */

import type { CustomFieldRender, CustomField } from '@puckeditor/core';
import type { CtaValue } from '@lp/shared';
import { KNOWN_ANALYTICS_IDS, isValidAnalyticsId } from '@lp/blocks';

const EMPTY: CtaValue = {
  label: '',
  href: 'https://app.collctiv.com/signup',
  analyticsId: '',
  location: 'website',
  utmContent: '',
};

export const CtaField: CustomFieldRender<CtaValue> = ({ value, onChange, readOnly }) => {
  const v: CtaValue = { ...EMPTY, ...(value ?? {}) };
  const set = (patch: Partial<CtaValue>): void => onChange({ ...v, ...patch });

  const idValid = !v.analyticsId || isValidAnalyticsId(v.analyticsId);

  return (
    <div className="lpf">
      <label className="lpf__row">
        <span className="lpf__label">Button text</span>
        <input
          className="lpf__input"
          value={v.label}
          disabled={readOnly}
          placeholder="Collect money now"
          onChange={(e) => set({ label: e.target.value })}
        />
      </label>

      <label className="lpf__row">
        <span className="lpf__label">Destination</span>
        <input
          className="lpf__input"
          value={v.href}
          disabled={readOnly}
          onChange={(e) => set({ href: e.target.value })}
        />
      </label>

      <label className="lpf__row">
        <span className="lpf__label">
          Analytics position
          <span className="lpf__hint">Becomes the button id and the PostHog position</span>
        </span>
        <input
          className={`lpf__input${idValid ? '' : ' lpf__input--error'}`}
          value={v.analyticsId}
          disabled={readOnly}
          list="lp-analytics-ids"
          placeholder="lpHeroCTA"
          onChange={(e) => set({ analyticsId: e.target.value })}
        />
        <datalist id="lp-analytics-ids">
          {KNOWN_ANALYTICS_IDS.map((id) => (
            <option key={id} value={id} />
          ))}
        </datalist>
        {!idValid && (
          <span className="lpf__error">
            Letters, numbers, dashes and underscores only, starting with a letter.
          </span>
        )}
      </label>

      <label className="lpf__row">
        <span className="lpf__label">
          utm_content
          <span className="lpf__hint">Leave blank to reuse the analytics position</span>
        </span>
        <input
          className="lpf__input"
          value={v.utmContent}
          disabled={readOnly}
          placeholder={v.analyticsId || 'lp_hero'}
          onChange={(e) => set({ utmContent: e.target.value })}
        />
      </label>
    </div>
  );
};

/** Typed shorthand for declaring a CTA field. */
export const ctaField = (label: string): CustomField<CtaValue> => ({
  type: 'custom',
  label,
  render: CtaField,
});
